import { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import useUser from '../auth/useUser';
import { getIncidents, updateIncident } from '../services/supportService';
import ReportIncidentModal from '../components/modals/ReportIncidentModal';

const STATUSES = ['Open', 'In-Progress', 'On-Hold', 'Completed', 'Cancelled'];

const STATUS_COLORS = {
  'Open':        { bg: '#e8f5e9', color: '#2e7d32' },
  'In-Progress': { bg: '#fff3e0', color: '#e65100' },
  'On-Hold':     { bg: '#e3f2fd', color: '#1565c0' },
  'Completed':   { bg: '#eceff1', color: '#546e7a' },
  'Cancelled':   { bg: '#fce4ec', color: '#c62828' },
};

const TH = { padding: '10px 14px', fontWeight: 600, color: 'var(--t2)' };
const TD = { padding: '10px 14px' };

function ticketNumber(row) {
  const year = new Date(row.created_at).getFullYear();
  return `INC-${year}-${String(row.id).padStart(4, '0')}`;
}

function truncate(str, len = 60) {
  if (!str) return '';
  return str.length > len ? str.slice(0, len) + '...' : str;
}

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${String(d.getDate()).padStart(2, '0')} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

function getSummary(row) {
  if (row.category === 'Report File Issue') {
    const parts = [row.supplier_name, row.document_name_reference].filter(Boolean);
    return parts.join(' / ') || '\u2014';
  }
  if (row.category === 'New User Creation') return row.new_user_text || '\u2014';
  return '\u2014';
}

// ─── Detail / Edit Modal ─────────────────────────────────────────
function IncidentDetailModal({ incident, isAdmin, onClose, onSaved, msalInstance }) {
  const [status, setStatus] = useState(incident.status || 'Open');
  const [resNotes, setResNotes] = useState(incident.resolution_notes || '');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const ticket = ticketNumber(incident);
  const sc = STATUS_COLORS[incident.status] || STATUS_COLORS.Open;
  const isTerminal = incident.status === 'Completed' || incident.status === 'Cancelled';
  const canEdit = isAdmin && !isTerminal;

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    try {
      await updateIncident(incident.id, { status, resolution_notes: resNotes }, msalInstance);
      onSaved();
    } catch (err) {
      setSaveError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const rows = [
    ['Ticket #', ticket],
    ['Date', formatDate(incident.created_at)],
    ['Requester', incident.requester_email],
    ['Category', incident.category],
  ];
  if (incident.supplier_name) rows.push(['Supplier', incident.supplier_name]);
  if (incident.document_name_reference) rows.push(['File', incident.document_name_reference]);
  if (incident.new_user_text) rows.push(['New User Details', incident.new_user_text]);
  if (incident.notes) rows.push(['Notes', incident.notes]);
  if (incident.attachment_original_name) rows.push(['Attachment', incident.attachment_original_name]);
  if (incident.status_updated_by) rows.push(['Last Updated By', incident.status_updated_by]);
  if (incident.status_updated_at) rows.push(['Last Updated', formatDate(incident.status_updated_at)]);

  return (
    <div className="modal-backdrop-fo" onClick={onClose}>
      <div className="modal-fo" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <button className="mcl" onClick={onClose}>&#10005;</button>
        <div className="m-title">{ticket}</div>
        <div className="m-sub">{incident.category}</div>

        <table style={{ width: '100%', fontSize: 13, marginTop: 12 }}>
          <tbody>
            {rows.map(([k, v]) => (
              <tr key={k}>
                <td style={{ padding: '5px 10px 5px 0', fontWeight: 600, color: 'var(--t2)', whiteSpace: 'nowrap', verticalAlign: 'top', width: 140 }}>{k}</td>
                <td style={{ padding: '5px 0', color: 'var(--t1)', wordBreak: 'break-word' }}>{v}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {isTerminal && (
          <div style={{ background: '#f5f5f5', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: 'var(--t3)', marginTop: 16 }}>
            This ticket is {incident.status} and can no longer be modified.
          </div>
        )}

        <div style={{ borderTop: '1px solid var(--brd)', marginTop: 16, paddingTop: 16 }}>
          <label className="flabel">Status</label>
          {canEdit ? (
            <select className="finput" value={status} onChange={e => setStatus(e.target.value)}>
              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          ) : (
            <div style={{ padding: '7px 0', fontSize: 13 }}>
              <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: sc.bg, color: sc.color }}>
                {incident.status || 'Open'}
              </span>
            </div>
          )}
        </div>

        <div style={{ marginTop: 12 }}>
          <label className="flabel">Resolution Notes</label>
          {canEdit ? (
            <textarea
              className="finput"
              rows={3}
              value={resNotes}
              onChange={e => setResNotes(e.target.value)}
              placeholder="Add resolution details..."
              style={{ resize: 'vertical' }}
            />
          ) : (
            <div style={{ fontSize: 13, color: incident.resolution_notes ? 'var(--t1)' : 'var(--t3)', padding: '7px 0', whiteSpace: 'pre-wrap' }}>
              {incident.resolution_notes || 'No resolution notes yet.'}
            </div>
          )}
        </div>

        {saveError && <div style={{ color: 'var(--red)', fontSize: 13, marginTop: 8 }}>{saveError}</div>}

        <div className="d-flex justify-content-end gap-2 pt-3 mt-3 border-top">
          <button className="btn btn-s" onClick={onClose} disabled={saving}>Close</button>
          {canEdit && (
            <button className="btn btn-p" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────
export default function ReportIncident() {
  const { email } = useUser();
  const { instance: msalInstance } = useMsal();
  const [incidents, setIncidents] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [detailRow, setDetailRow] = useState(null);

  const loadIncidents = useCallback(async () => {
    if (!email) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await getIncidents(msalInstance);
      setIncidents(resp.incidents);
      setIsAdmin(resp.isAdmin);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [email, msalInstance]);

  useEffect(() => { loadIncidents(); }, [loadIncidents]);

  const handleCreated = () => {
    setShowCreateModal(false);
    loadIncidents();
  };

  const handleDetailSaved = () => {
    setDetailRow(null);
    loadIncidents();
  };

  return (
    <div className="p-4">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h4 style={{ fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Report Incident</h4>
          <p style={{ color: 'var(--t3)', fontSize: 13, margin: 0 }}>
            {isAdmin ? 'All support incidents (admin view)' : 'View and create support incidents'}
          </p>
        </div>
        <button className="btn btn-p" onClick={() => setShowCreateModal(true)}>
          <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" style={{ marginRight: 6 }}>
            <path d="M12 5v14M5 12h14" />
          </svg>
          New
        </button>
      </div>

      <div className="card" style={{ borderRadius: 'var(--r2)', border: '1px solid var(--brd)', overflow: 'hidden' }}>
        {loading ? (
          <div className="text-center py-5" style={{ color: 'var(--t3)' }}>Loading incidents...</div>
        ) : error ? (
          <div className="text-center py-5" style={{ color: 'var(--red)' }}>
            <p style={{ margin: 0 }}>Failed to load incidents</p>
            <p style={{ fontSize: 12, margin: '4px 0 0' }}>{error}</p>
            <button className="btn btn-s mt-2" onClick={loadIncidents}>Retry</button>
          </div>
        ) : incidents.length === 0 ? (
          <div className="text-center py-5" style={{ color: 'var(--t3)' }}>
            <p style={{ margin: 0, fontSize: 14 }}>No incidents found</p>
            <p style={{ fontSize: 12, margin: '4px 0 0' }}>Click "New" to create your first incident.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover mb-0" style={{ fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--surf)', borderBottom: '2px solid var(--brd)' }}>
                  <th style={TH}>Ticket #</th>
                  <th style={TH}>Date</th>
                  {isAdmin && <th style={TH}>Requester</th>}
                  <th style={TH}>Category</th>
                  <th style={TH}>Summary</th>
                  <th style={TH}>Status</th>
                  <th style={TH}>Resolution Notes</th>
                </tr>
              </thead>
              <tbody>
                {incidents.map((row) => {
                  const sc = STATUS_COLORS[row.status] || STATUS_COLORS.Open;
                  return (
                    <tr key={row.id} onClick={() => setDetailRow(row)} style={{ cursor: 'pointer' }}>
                      <td style={{ ...TD, fontWeight: 600, whiteSpace: 'nowrap', color: 'var(--teal)' }}>{ticketNumber(row)}</td>
                      <td style={{ ...TD, whiteSpace: 'nowrap' }}>{formatDate(row.created_at)}</td>
                      {isAdmin && <td style={TD}>{row.requester_email}</td>}
                      <td style={TD}>{row.category}</td>
                      <td style={{ ...TD, maxWidth: 220 }}>{truncate(getSummary(row), 50)}</td>
                      <td style={TD}>
                        <span style={{
                          display: 'inline-block', padding: '2px 10px', borderRadius: 12,
                          fontSize: 11, fontWeight: 600, background: sc.bg, color: sc.color,
                        }}>
                          {row.status || 'Open'}
                        </span>
                      </td>
                      <td style={{ ...TD, maxWidth: 200, color: row.resolution_notes ? 'var(--t1)' : 'var(--t3)' }}>
                        {truncate(row.resolution_notes, 40) || '\u2014'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ReportIncidentModal
        show={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={handleCreated}
        userEmail={email}
        msalInstance={msalInstance}
      />

      {detailRow && (
        <IncidentDetailModal
          incident={detailRow}
          isAdmin={isAdmin}
          onClose={() => setDetailRow(null)}
          onSaved={handleDetailSaved}
          msalInstance={msalInstance}
        />
      )}
    </div>
  );
}
